// Lightweight upsell metadata shared by the product bundle (view-data variable
// resolution in features/upsell) and the admin-facing registry. Kept apart from
// registry.ts so the client never imports the ~3k-line field-descriptor file.

export type UpsellRegistryEntry = { id: string; type: string; sale: boolean };

// The 31k prompts pack, in the order its screens were sold under: what the
// bundles list as a line, and the Claude / creative bundles give away. Its own
// live price (/students/upsells-prices) is the line's price and the gift's
// struck-through one.
export const PROMPTS_PACK_UPSELL_IDS = ["30k_prompts_complete_pack", "30k_prompts"] as const;

// One entry per app/upsell/<id>/page.tsx route directory. `sale` mirrors the
// `sale={true}` prop the page passes to its screen component.
export const upsellRegistry: UpsellRegistryEntry[] = [
    { id: "30k_prompts", type: "ultimate_ai_pack", sale: false },
    { id: "30k_prompts_complete_pack", type: "complete_ai_pack", sale: false },
    { id: "30k_prompts_complete_pack_sale", type: "complete_ai_pack", sale: true },
    // Renders CompleteAiPack, not UltimateAiPack, despite the shared base name.
    { id: "30k_prompts_sale", type: "complete_ai_pack", sale: true },
    { id: "600_prompts", type: "personal_boost_pack", sale: false },
    { id: "600_prompts_sale", type: "personal_boost_pack", sale: true },
    // ai_tools(_sale) A/B between the aiTools.tsx control and aiToolsV2.tsx
    // (Amplitude flag); both variants read the ai_tools_v2 descriptors.
    { id: "ai_tools", type: "ai_tools_v2", sale: false },
    { id: "ai_tools_sale", type: "ai_tools_v2", sale: true },
    { id: "bundle", type: "public_bundle", sale: false },
    { id: "claude_work_bundle", type: "claude_work_bundle", sale: false },
    { id: "claude_work_bundle_sale", type: "claude_work_bundle", sale: true },
    { id: "complete_bundle", type: "complete_bundle", sale: false },
    { id: "drama_writers_room", type: "drama_skill", sale: false },
    { id: "drama_writers_room_sale", type: "drama_skill", sale: true },
    { id: "drama_income_kit", type: "drama_income_kit", sale: false },
    { id: "drama_complete_bundle", type: "drama_complete_bundle", sale: false },
    { id: "film_studio", type: "film_skill", sale: false },
    { id: "film_studio_sale", type: "film_skill", sale: true },
    { id: "film_income_kit", type: "film_income_kit", sale: false },
    { id: "film_complete_bundle", type: "film_complete_bundle", sale: false }
];

// Product names as the customer knows them (they match the Solidgate product
// names on the receipt). Used where an upsell has to be named outside its own
// screen — e.g. a 3D Secure challenge that surfaces after the funnel moved on.
// Sale variants are the same product at a different price.
// Since plan metadata iteration 2 the name comes from the admin's plan
// metadata (`name`, delivered by /students/upsells-prices already localized);
// this map is the fallback while a plan has none typed.
const UPSELL_DISPLAY_NAMES: Record<string, string> = {
    "30k_prompts": "The Ultimate AI Power Pack",
    "30k_prompts_sale": "The Ultimate AI Power Pack",
    "30k_prompts_complete_pack": "The Complete AI Income Prompt Pack",
    "30k_prompts_complete_pack_sale": "The Complete AI Income Prompt Pack",
    "600_prompts": "Personal Boost Pack",
    "600_prompts_sale": "Personal Boost Pack",
    ai_tools: "AI-tools Unlimited Access",
    ai_tools_sale: "AI-tools Unlimited Access",
    bundle: "Bundle 3 in 1",
    claude_work_bundle: "The Complete Claude Work Bundle",
    claude_work_bundle_sale: "The Complete Claude Work Bundle",
    complete_bundle: "The Complete Bundle",
    drama_writers_room: "Drama Writers’ Room",
    drama_writers_room_sale: "Drama Writers’ Room",
    drama_income_kit: "Drama Shorts Income Kit",
    drama_complete_bundle: "The Complete Bundle",
    film_studio: "AI Film Studio",
    film_studio_sale: "AI Film Studio",
    film_income_kit: "AI Film Income Kit",
    film_complete_bundle: "The Complete Bundle"
};

export const getUpsellDisplayName = (upsellId: string): string | null => UPSELL_DISPLAY_NAMES[upsellId] ?? null;

// Per-type percent fallbacks for {{percent}} in view_data strings while the
// plan has no typed discount, mirroring each screen component's own default
// (the badge). view_data carries no percent of its own; registry.test.ts
// pins these to the registry's type list.
export const SALE_PERCENT_DEFAULTS: Record<string, { default?: number; saleDefault?: number }> = {
    ultimate_ai_pack: { default: 50, saleDefault: 60 },
    complete_ai_pack: { default: 50, saleDefault: 60 },
    // one percent on both Boost Pack pages: the badge and the strikethrough share it
    personal_boost_pack: { default: 70 },
    ai_tools_v2: { default: 50, saleDefault: 70 },
    claude_work_bundle: { default: 50, saleDefault: 60 },
    complete_bundle: { default: 83 }
};
